import type { LoaderFunctionArgs } from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export const PLAYGROUND_LOADER_ID = "playgroundLoader";

type TestRun = Database["public"]["Tables"]["test_runs"]["Row"];
type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type TestRunWithProfile = TestRun & {
  profiles?: Pick<Profile, "display_name"> | null;
};
type PgflowRunStatus =
  Database["public"]["Views"]["pgflow_run_statuses"]["Row"];

export interface PlaygroundTestRun extends TestRun {
  display_name?: string | null;
  pgflow_status?: string | null;
  pgflow_latest_step_slug?: string | null;
}

export interface PlaygroundLoaderData {
  project: {
    id: string;
    title: string;
    agentId: string | null;
    codeId: string | null;
  };
  testRuns: PlaygroundTestRun[];
}

export const playgroundLoader = async ({
  params,
}: LoaderFunctionArgs): Promise<PlaygroundLoaderData> => {
  // Support both projectId and evalId params
  const projectId = params["projectId"] || params["evalId"];
  if (!projectId) {
    throw new Response("Project ID required", { status: 400 });
  }

  // Fetch project details
  const { data: project, error: projectError } = await supabase
    .from("projects")
    .select("id, title, agent_id, code_id")
    .eq("id", projectId)
    .single();

  if (projectError || !project) {
    throw new Response("Project not found", { status: 404 });
  }

  const testRunsPromise = supabase
    .from("test_runs")
    .select("*, profiles(display_name)")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });
  const specRunLinksPromise = supabase
    .from("specification_run_test_runs")
    .select("test_run_id, specification_runs!inner(project_id)")
    .eq("specification_runs.project_id", projectId);
  const pgflowStatusesPromise = supabase
    .from("pgflow_run_statuses")
    .select("*")
    .eq("project_id", projectId);

  const [
    { data: testRunsData, error: testRunsError },
    { data: specRunLinksData, error: specRunLinksError },
    { data: pgflowStatuses, error: pgflowStatusesError },
  ] = await Promise.all([
    testRunsPromise,
    specRunLinksPromise,
    pgflowStatusesPromise,
  ]);

  if (testRunsError) {
    throw testRunsError;
  }
  if (specRunLinksError) {
    throw specRunLinksError;
  }
  if (pgflowStatusesError) {
    throw pgflowStatusesError;
  }

  const pgflowMap = new Map<string, PgflowRunStatus>();
  pgflowStatuses?.forEach((status) => {
    if (status.test_run_id) {
      pgflowMap.set(status.test_run_id, status);
    }
  });

  // Combine test runs with profiles and pgflow statuses
  const specRunIds = new Set(
    (specRunLinksData || []).map((link) => link.test_run_id),
  );
  const typedRuns = (testRunsData || []) as unknown as TestRunWithProfile[];
  const filteredRuns = typedRuns.filter((run) => !specRunIds.has(run.id));
  const testRuns: PlaygroundTestRun[] = filteredRuns.map((run) => {
    const pgflowStatus = pgflowMap.get(run.id);

    return {
      ...run,
      display_name: run.profiles?.display_name || null,
      pgflow_status: pgflowStatus?.pgflow_status || null,
      pgflow_latest_step_slug: pgflowStatus?.latest_step_slug || null,
    };
  });

  return {
    project: {
      id: project.id,
      title: project.title,
      agentId: project.agent_id,
      codeId: project.code_id,
    },
    testRuns,
  };
};
